import { useCallback, useEffect, useState } from 'react';
import { api, operateur, sessionSignature } from '../api.js';
import { Signature } from './Signature.jsx';

/**
 * Parcours de levée des réserves.
 *
 * Utilisateur différent de celui du formulaire de réception : c'est le conducteur de
 * travaux, souvent au bureau, qui part d'une liste d'affaires bloquées plutôt que
 * d'un formulaire vierge. D'où une entrée par la file de travail et non par la saisie.
 *
 * La levée est une SECONDE demande de signature, distincte de la réception :
 *  - elle porte sur un document régénéré comportant la date de levée ;
 *  - le procès-verbal de réception signé y est joint en annexe ;
 *  - elle est reliée à la première par l'external_id de la fiche.
 * Le formulaire papier mélangeait les deux dans un seul document, ce qui laissait des
 * fiches ouvertes pendant des mois.
 */


export function LeveeReserves({ sandbox, onQuitter }) {
  const [etat, setEtat] = useState('chargement'); // chargement | liste | vide | saisie | signature | fini | echec
  const [fiches, setFiches] = useState([]);
  const [choisie, setChoisie] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  // Filtre « mes affaires » : coché par défaut sur le matricule de l'opérateur.
  // La liste reste complète (rien n'est masqué durablement) — on ne fait que
  // restreindre l'affichage, décochable à tout moment.
  const [filtreMes, setFiltreMes] = useState(true);
  const matriculeCourant = operateur.courant();

  const charger = useCallback(async () => {
    setEtat('chargement');
    setErreur(null);
    try {
      const { fiches: liste } = await api.listerAttenteLevee();
      setFiches(liste);
      setEtat(liste.length ? 'liste' : 'vide');
    } catch (err) {
      setErreur(err.message);
      setEtat('echec');
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  /** Reprise d'une levée déjà partie en signature, sans en relancer une seconde. */
  const reprendre = (fiche) => {
    setChoisie(fiche);
    setEtat('signature');
  };

  const lancer = async (mode) => {
    setEnvoi(true);
    setErreur(null);
    try {
      await api.lancerLevee(choisie.id, mode);
      if (mode === 'distance') {
        setEtat('envoye');
      } else {
        // Trace persistée avant l'affichage de l'iframe, comme pour la réception :
        // une coupure à cet instant ne doit pas rendre la levée introuvable.
        sessionSignature.enregistrer({
          ficheId: choisie.id,
          reference: choisie.reference,
          nomAffaire: choisie.nomAffaire,
          appareilNo: choisie.appareilNo,
          phase: 'levee',
        });
        setEtat('signature');
      }
    } catch (err) {
      setErreur(
        err.erreurs?.map((e) => e.message).join(' · ') ?? err.message,
      );
    } finally {
      setEnvoi(false);
    }
  };

  const terminer = useCallback(() => {
    sessionSignature.effacer();
    setEtat('fini');
  }, []);

  /* --- Rendu --------------------------------------------------------- */

  if (etat === 'chargement') {
    return <div className="etat"><div className="titre">Chargement des affaires…</div></div>;
  }

  if (etat === 'echec') {
    return (
      <div className="etat">
        <div className="titre">Liste indisponible</div>
        <p className="sous-titre">{erreur}</p>
        <button type="button" className="bouton-reprise" onClick={charger}>Réessayer</button>
      </div>
    );
  }

  if (etat === 'vide') {
    return (
      <div className="etat succes">
        <div className="titre">Aucune réserve en attente</div>
        <p className="sous-titre">
          Toutes les réceptions prononcées avec réserves ont vu leur levée signée.
        </p>
        <button type="button" className="bouton-reprise" onClick={onQuitter}>Retour</button>
      </div>
    );
  }

  if (etat === 'fini') {
    return (
      <>
        <div className="etat succes">
          <div className="titre">Levée signée</div>
          <p className="sous-titre">
            Réf. {choisie.reference}. La réception est désormais définitive : l'affaire
            peut être soldée. Le document signé et son dossier de preuves sont archivés
            automatiquement.
          </p>
        </div>
        <nav className="barre">
          <button type="button" onClick={() => { setChoisie(null); charger(); }}>
            Autre levée
          </button>
          <button type="button" className="primaire" onClick={onQuitter}>
            Retour à l'accueil
          </button>
        </nav>
      </>
    );
  }

  if (etat === 'signature') {
    return (
      <Signature
        ficheId={choisie.id}
        phase="levee"
        sandbox={sandbox}
        onTermine={terminer}
        onErreur={(message) => setErreur(message)}
      />
    );
  }

  if (etat === 'saisie') {
    return (
      <>
        <h2>Confirmer la levée des réserves</h2>
        <p className="sous-titre">
          {choisie.nomAffaire} — appareil {choisie.appareilNo}, réf. {choisie.reference}.
        </p>

        <div className="carte">
          <div className="rappel-reserves">
            <div className="intitule-reserves">Réserves prononcées le {choisie.dateReception}</div>
            <p>{choisie.natureReserves}</p>
          </div>
          <p className="sous-titre">
            La levée se signe directement sur le procès-verbal de réception déjà signé.
            La date de levée est celle de la signature. En signant, les parties prononcent
            la réception définitive.
          </p>
          {erreur && <span className="erreur" role="alert">{erreur}</span>}
        </div>

        <div className="carte">
          <button
            type="button"
            className="bouton-accueil"
            disabled={envoi}
            onClick={() => lancer('presentiel')}
          >
            <span className="bouton-accueil-titre">Signer ensemble, maintenant</span>
            <span className="bouton-accueil-detail">
              Le client est présent. Signature sur cet appareil.
            </span>
          </button>
          <button
            type="button"
            className="bouton-accueil"
            disabled={envoi}
            onClick={() => lancer('distance')}
          >
            <span className="bouton-accueil-titre">Envoyer à signer à distance</span>
            <span className="bouton-accueil-detail">
              Fréquent pour une levée sur photos. Chaque partie signe de son côté, par e-mail.
            </span>
          </button>
        </div>

        <nav className="barre">
          <button type="button" onClick={() => { setChoisie(null); setEtat('liste'); setErreur(null); }}>
            Retour
          </button>
        </nav>
      </>
    );
  }

  if (etat === 'envoye') {
    return (
      <>
        <div className="etat succes">
          <div className="titre">Invitations envoyées</div>
          <p className="sous-titre">
            Réf. {choisie.reference}. Chaque partie va recevoir un e-mail l'invitant à signer
            la levée, dans l'ordre : le client d'abord, puis le représentant Etna. Vous serez
            notifié quand tout sera signé. Suivi et relance dans « Signatures en attente ».
          </p>
        </div>
        <nav className="barre">
          <button type="button" onClick={() => { setChoisie(null); charger(); }}>
            Autre levée
          </button>
          <button type="button" className="primaire" onClick={onQuitter}>
            Retour à l'accueil
          </button>
        </nav>
      </>
    );
  }

  const mesAffaires = matriculeCourant
    ? fiches.filter((f) => f.receptionnePar === matriculeCourant)
    : [];
  const affichees = filtreMes && matriculeCourant ? mesAffaires : fiches;

  return (
    <>
      <h2>Réserves à lever</h2>
      <p className="sous-titre">
        {fiches.length} affaire{fiches.length > 1 ? 's' : ''} dont la réception n'est pas
        définitive. Chacune bloque le solde de son affaire.
      </p>

      {matriculeCourant && (
        <label className="filtre-mes">
          <input
            type="checkbox"
            checked={filtreMes}
            onChange={(ev) => setFiltreMes(ev.target.checked)}
          />
          <span>
            Afficher seulement mes affaires ({matriculeCourant}) — {mesAffaires.length} sur {fiches.length}
          </span>
        </label>
      )}

      {affichees.length === 0 ? (
        <div className="etat">
          <div className="titre">Aucune affaire à votre matricule</div>
          <p className="sous-titre">
            Aucune réserve en attente n'a été réceptionnée sous « {matriculeCourant} ».
            Décochez le filtre ci-dessus pour voir toutes les affaires en attente.
          </p>
        </div>
      ) : affichees.map((f) => (
        <div className="carte affaire" key={f.id}>
          <div className="affaire-titre">{f.nomAffaire}</div>
          <div className="affaire-meta">
            Appareil {f.appareilNo} · réf. {f.reference} · réception du {f.dateReception}
            {f.affaireNo ? ` · affaire ${f.affaireNo}` : ''}
          </div>
          {f.receptionnePar && (
            <div className="affaire-receptionnaire">Réceptionné par {f.receptionnePar}</div>
          )}
          <p className="affaire-reserves">{f.natureReserves}</p>
          {f.leveeEnCours ? (
            <>
              <div className="etiquette">Levée déjà envoyée en signature</div>
              <button type="button" className="bouton-affaire" onClick={() => reprendre(f)}>
                Reprendre la signature
              </button>
            </>
          ) : (
            <button
              type="button"
              className="bouton-affaire"
              onClick={() => {
              setChoisie(f);
              setEtat('saisie');
              setErreur(null);
            }}
            >
              Lever les réserves
            </button>
          )}
        </div>
      ))}

      <nav className="barre">
        <button type="button" onClick={onQuitter}>Retour</button>
      </nav>
    </>
  );
}
